// ST-32 -Claude- Admin dashboard. Counts come from authenticated Nearrme API endpoints.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { useAdminAuth } from '../useAdminAuth';
import { getAgentStatus, isNearrmeAdminRole, listAuditLogs, listBrands, listCompanies, listProducts, listUsers } from '../api';

interface StatCard {
  label: string;
  value: string | number;
  loading?: boolean;
  error?: string | null;
  helper?: string;
  to: string;
}

function Stat({ label, value, loading, error, helper, to }: StatCard) {
  return (
    <Link to={to} className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
    <Card padded className="border border-slate-200 dark:border-slate-800 shadow-sm transition hover:border-brand-300 hover:shadow-md dark:hover:border-brand-700">
      <div className="flex min-h-28 flex-col justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
          {loading ? (
            <LoadingSkeleton className="h-8 w-20 mt-2" />
          ) : error ? (
            <div className="text-xs text-red-500 mt-2 max-w-[180px]" title={error}>{error}</div>
          ) : (
            <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">{value}</div>
          )}
        </div>
        {helper && <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">{helper}</div>}
      </div>
    </Card>
    </Link>
  );
}

export function AdminDashboardPage() {
  const { user } = useAdminAuth();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const companyCode = isNearrmeAdmin ? '__all' : user?.companyCode ?? '';
  const pageTitle = isNearrmeAdmin ? 'Nearrme Admin Dashboard' : 'Business Dashboard';
  const scopeText = isNearrmeAdmin
    ? 'Network-wide operational view across registered businesses.'
    : `Scoped to ${user?.companyCode ?? 'your assigned business'}.`;

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const brands = useQuery({
    queryKey: ['admin', 'brands', companyCode],
    queryFn: () => listBrands(isNearrmeAdmin ? '' : companyCode),
    enabled: !!companyCode,
  });
  const products = useQuery({
    queryKey: ['admin', 'products', companyCode],
    queryFn: () => listProducts(companyCode),
    enabled: !!companyCode,
  });
  const users = useQuery({
    queryKey: ['admin', 'users', companyCode],
    queryFn: () => listUsers(companyCode),
    enabled: !!companyCode,
  });
  const agents = useQuery({
    queryKey: ['admin', 'agent-status'],
    queryFn: getAgentStatus,
    enabled: isNearrmeAdmin,
  });
  const activity = useQuery({
    queryKey: ['admin', 'dashboard-activity'],
    queryFn: () => listAuditLogs(undefined, new Date().toISOString().slice(0, 10)),
  });

  return (
    <div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              {isNearrmeAdmin ? 'Admin mode' : 'Business owner mode'}
            </div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">{pageTitle}</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              {scopeText} The same console is shared, but data access is restricted by role and company assignment.
            </p>
          </div>
          <div className="rounded-md border border-slate-200 dark:border-slate-800 px-4 py-3 text-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Signed in as</div>
            <div className="mt-1 font-medium text-slate-900 dark:text-slate-100">{user?.email}</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{user?.role} / {user?.companyCode}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label={isNearrmeAdmin ? 'Businesses' : 'Business'}
          value={companies.data?.length ?? '-'}
          loading={companies.isLoading}
          error={companies.error ? (companies.error as Error).message : null}
          helper={isNearrmeAdmin ? 'All listed businesses' : 'Your assigned business'}
          to="/admin/companies"
        />
        <Stat
          label="Brands"
          value={brands.data?.length ?? '-'}
          loading={brands.isLoading}
          error={brands.error ? (brands.error as Error).message : null}
          helper="Verified manufacturers"
          to="/admin/brands"
        />
        <Stat
          label="Products"
          value={products.data?.length ?? '-'}
          loading={products.isLoading}
          error={products.error ? (products.error as Error).message : null}
          helper="Active catalog items"
          to="/admin/products"
        />
        <Stat
          label="People"
          value={users.data?.length ?? '-'}
          loading={users.isLoading}
          error={users.error ? (users.error as Error).message : null}
          helper="Team and consumers"
          to={isNearrmeAdmin ? '/admin/nearrme-team' : '/admin/company-team'}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link to="/admin/contact-messages" className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
          <Card padded className="h-full border border-slate-200 dark:border-slate-800 shadow-sm transition hover:border-brand-300 hover:shadow-md dark:hover:border-brand-700">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Messages</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  Contact Us and business follow-up messages that need review, assignment, or resolution.
                </p>
              </div>
              <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                Open
              </span>
            </div>
          </Card>
        </Link>

        <Link to="/admin/setup/activity" className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
          <Card padded className="h-full border border-slate-200 dark:border-slate-800 shadow-sm transition hover:border-brand-300 hover:shadow-md dark:hover:border-brand-700">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Activity</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  Latest security and portal events for sign-ins, role use, and operational activity.
                </p>
                <div className="mt-4 text-3xl font-semibold text-slate-950 dark:text-slate-50">
                  {activity.isLoading ? '-' : activity.data?.length ?? 0}
                </div>
              </div>
              <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                Open
              </span>
            </div>
          </Card>
        </Link>

        {isNearrmeAdmin && (
          <Card padded className="border border-slate-200 dark:border-slate-800 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Operational services
            </h2>
            {agents.isLoading && <LoadingSkeleton className="h-20 w-full" />}
            {agents.error && (
              <div className="text-sm text-red-500">
                {(agents.error as Error).message}
              </div>
            )}
            {agents.data && (
              <div className="space-y-2">
                {(agents.data.providers ?? []).length === 0 ? (
                  <EmptyState title="No providers configured" description="No AI providers reported by /api/v2/agents/status." />
                ) : (
                  (agents.data.providers ?? []).map((p) => (
                    <div key={p.name} className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2 text-sm">
                      <div>
                        <div className="font-medium text-slate-900 dark:text-slate-100">{p.name}</div>
                        {p.model && <div className="text-xs text-slate-500 dark:text-slate-400">{p.model}</div>}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${p.isAvailable ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'}`}>
                        {p.isAvailable ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padded className="border border-slate-200 dark:border-slate-800 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Access model
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Nearrme Admin users can operate across the full network. Business Owner users see the same product surface,
            but business, product, customer, and message data is filtered to the assigned company code.
          </p>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Current API: <code>{(import.meta.env.VITE_API_URL ?? 'local').replace(/^https?:\/\//, '')}</code>
          </p>
        </Card>
      </div>
    </div>
  );
}
